import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../../services/api';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    url: '',
    start_date: '',
    end_date: '',
    is_current: false,
    technologies: [''],
    description: [''],
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    setFormData({
      name: '',
      client: '',
      url: '',
      start_date: '',
      end_date: '',
      is_current: false,
      technologies: [''],
      description: [''],
    });
    setShowModal(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      client: project.client || '',
      url: project.url || '',
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      end_date: project.end_date ? project.end_date.split('T')[0] : '',
      is_current: project.is_current,
      technologies: project.technologies && project.technologies.length > 0 ? project.technologies : [''],
      description: project.description && project.description.length > 0 ? project.description : [''],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectsAPI.delete(id);
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const technologiesArray = formData.technologies.filter(t => t.trim() !== '');
      const descriptionArray = formData.description.filter(d => d.trim() !== '');
      const data = {
        name: formData.name,
        client: formData.client,
        url: formData.url || null,
        start_date: formData.start_date || null,
        end_date: formData.is_current ? null : (formData.end_date || null),
        is_current: formData.is_current,
        technologies: technologiesArray,
        description: descriptionArray,
      };

      if (editingProject) {
        await projectsAPI.update(editingProject.id, data);
      } else {
        await projectsAPI.create(data);
      }
      setShowModal(false);
      loadProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project');
    } finally {
      setLoading(false);
    }
  };

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const removeArrayItem = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray.length > 0 ? newArray : [''] });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-gray-800 dark:text-gray-200 text-2xl font-bold">Projects</h2>
        <button className="btn btn-primary dark:bg-gradient-to-r dark:from-purple-600 dark:to-purple-700" onClick={handleAdd}>Add Project</button>
      </div>

      <div className="item-list">
        {projects.map((project) => (
          <div key={project.id} className="item-card">
            <div className="item-card-content">
              <h3>{project.name}</h3>
              {project.client && <p><strong>Client:</strong> {project.client}</p>}
              {project.url && (
                <p>
                  <strong>URL:</strong> <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{project.url}</a>
                </p>
              )}
              {project.start_date && (
                <p>
                  {new Date(project.start_date).toLocaleDateString()} - {project.is_current ? 'Present' : (project.end_date ? new Date(project.end_date).toLocaleDateString() : '')}
                </p>
              )}
              {project.technologies && project.technologies.length > 0 && (
                <p><strong>Technologies:</strong> {project.technologies.join(', ')}</p>
              )}
            </div>
            <div className="item-card-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(project)}>Edit</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(project.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-gray-800 dark:text-gray-200">{editingProject ? 'Edit Project' : 'Add Project'}</h2>
              <button className="close-btn dark:text-gray-300 dark:hover:text-gray-100" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200">Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                />
              </div>
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200">Client</label>
                <input
                  type="text"
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                />
              </div>
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200">Project URL</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://example.com"
                  className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                />
                <small className="text-gray-500 dark:text-gray-400 block mt-1">Optional: Enter the project's website URL</small>
              </div>
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                />
              </div>
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_current}
                    onChange={(e) => setFormData({ ...formData, is_current: e.target.checked, end_date: '' })}
                    className="dark:accent-purple-500"
                  />
                  Current Project
                </label>
              </div>
              {!formData.is_current && (
                <div className="form-group">
                  <label className="text-gray-800 dark:text-gray-200">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                  />
                </div>
              )}
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200">Technologies</label>
                {formData.technologies.map((tech, index) => (
                  <div key={index} className="array-input-item">
                    <input
                      type="text"
                      value={tech}
                      onChange={(e) => updateArrayItem('technologies', index, e.target.value)}
                      placeholder="Enter technology"
                      className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                    />
                    {formData.technologies.length > 1 && (
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => removeArrayItem('technologies', index)}>Remove</button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayItem('technologies')} style={{ marginTop: '0.5rem' }}>
                  Add Technology
                </button>
              </div>
              <div className="form-group">
                <label className="text-gray-800 dark:text-gray-200">Description</label>
                {formData.description.map((desc, index) => (
                  <div key={index} className="array-input-item">
                    <input
                      type="text"
                      value={desc}
                      onChange={(e) => updateArrayItem('description', index, e.target.value)}
                      placeholder="Enter description point"
                      className="dark:bg-slate-600 dark:text-gray-200 dark:border-slate-500"
                    />
                    {formData.description.length > 1 && (
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => removeArrayItem('description', index)}>Remove</button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => addArrayItem('description')} style={{ marginTop: '0.5rem' }}>
                  Add Description Point
                </button>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;


